
import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';

import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';

import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';

/** =========================
 * REQUEST
 * ========================= */
type PurTransactionsRequestDto = {
  pageIndex?: number;
  pageSize?: number;

  view?: string | null; // DETAIL | DISCOUNT_BY_SUPPLIER | DISCOUNT_BY_SUPPLIER_BRANCH | VAT_BY_BRANCH | SUMMARY_BY_TYPE

  // modern
  fromDate?: string | null;
  toDate?: string | null;

  branchId?: string | null;
  inventoryId?: string | null;
  peopleId?: string | null;
  transTypeIds?: string | null; // "id1,id2"

  // legacy duplicates (API sometimes expects these)
  froM_DATE?: string | null;
  tO_DATE?: string | null;
  brancH_ID?: string | null;
  inventorY_ID?: string | null;
  peoplE_ID?: string | null;
  tranS_TYPE_IDS?: string | null;
};

/** =========================
 * DETAIL ROW (view=DETAIL)
 * ========================= */
type DetailRowDto = {
  id: string | null;
  code: string | null;
  transactioN_DATE: string | null;
  description: string | null;

  grosS_AMOUNT: number;
  discounT_AMOUNT: number;
  vat: number;
  neT_AMOUNT: number;

  tranS_TYPE_ID: string | null;
  typE_NAME1: string | null; // AR
  typE_NAME2: string | null; // EN

  brancH_NAME: string | null;
  brancH_NAME2: string | null;

  inventorY_NAME: string | null;
  inventorY_NAME2: string | null;

  peoplE_ID: string | null;
  peoplE_NUMBER: string | null;
  peoplE_NAME: string | null;
  peoplE_NAME2: string | null;
};

const REPORT_KEY = 'RPT_PurTransactions';
const ENDPOINT = 'FinReports/GetPurTransactions';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number | null | undefined, digits: number = 2) => {
  if (num === null || num === undefined) return '0.00';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    num || 0
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v; // keep 0/false
  }
  return out;
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pickNum = (meta: any, keys: string[]) => {
  if (!meta) return 0;
  for (const k of keys) {
    if (meta?.[k] !== undefined && meta?.[k] !== null) return toNum(meta[k]);
  }
  return 0;
};

type ViewKey =
  | 'DETAIL'
  | 'DISCOUNT_BY_SUPPLIER'
  | 'DISCOUNT_BY_SUPPLIER_BRANCH'
  | 'VAT_BY_BRANCH'
  | 'SUMMARY_BY_TYPE';

const PurTransactions_Paged: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const dispatch = useAppDispatch();

  // Branch options
  const branchHook = useGetOperatingUnitWithPermission() as any;
  const OptionsBranches = branchHook?.OperatingUnitOptions ?? branchHook?.OptionsBranches ?? [];
  const loadingBranches = branchHook?.loading ?? false;

  // Supplier options (peopleId)
  const suppHook = useGetSuppliersList() as any;
  const OptionsSuppliers = suppHook?.OptionSuppliers ?? suppHook?.OptionsSuppliers ?? suppHook?.options ?? [];
  const loadingSuppliers = suppHook?.loading ?? false;

  // Filters
  const viewOptions = useMemo(
    () => [
      { value: 'DETAIL', label: isAr ? 'تفصيلي' : 'Detail' },
      { value: 'DISCOUNT_BY_SUPPLIER', label: isAr ? 'خصومات حسب المورد' : 'Discount by Supplier' },
      { value: 'DISCOUNT_BY_SUPPLIER_BRANCH', label: isAr ? 'خصومات حسب المورد/الفرع' : 'Discount by Supplier/Branch' },
      { value: 'VAT_BY_BRANCH', label: isAr ? 'الضريبة حسب الفرع' : 'VAT by Branch' },
      { value: 'SUMMARY_BY_TYPE', label: isAr ? 'ملخص حسب النوع' : 'Summary by Type' }
    ],
    [isAr]
  );

  const [view, setView] = useState<any>(viewOptions[0]); // default DETAIL
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [supplierFilter, setSupplierFilter] = useState<any>(null);

  const [inventoryId, setInventoryId] = useState<string>(''); // GUID (اختياري)
  const [transTypeIds, setTransTypeIds] = useState<string>(''); // CSV GUIDs (اختياري)

  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const itemsAny = (report?.items ?? []) as any[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;

  const currentView: ViewKey = (view?.value ?? 'DETAIL') as ViewKey;
  const isDetail = currentView === 'DETAIL';

  const itemsDetail = itemsAny as DetailRowDto[];

  // ===== Export Columns =====
  const exportColumns: ExportColumn<any>[] = useMemo(() => {
    if (isDetail) {
      const cols: ExportColumn<DetailRowDto>[] = [
        { headerAr: 'التاريخ', headerEn: 'Date', value: r => formatDate(r.transactioN_DATE) },
        { headerAr: 'الكود', headerEn: 'Code', value: r => r.code || '' },
        { headerAr: 'النوع', headerEn: 'Type', value: r => (isAr ? r.typE_NAME1 || r.typE_NAME2 : r.typE_NAME2 || r.typE_NAME1) || '' },
        {
          headerAr: 'الفرع',
          headerEn: 'Branch',
          value: r => (isAr ? r.brancH_NAME2 || r.brancH_NAME : r.brancH_NAME || r.brancH_NAME2) || ''
        },
        {
          headerAr: 'المخزن',
          headerEn: 'Inventory',
          value: r => (isAr ? r.inventorY_NAME2 || r.inventorY_NAME : r.inventorY_NAME || r.inventorY_NAME2) || ''
        },
        {
          headerAr: 'المورد',
          headerEn: 'Supplier',
          value: r => (isAr ? r.peoplE_NAME2 || r.peoplE_NAME : r.peoplE_NAME || r.peoplE_NAME2) || ''
        },
        { headerAr: 'الإجمالي', headerEn: 'Gross', value: r => r.grosS_AMOUNT || 0 },
        { headerAr: 'الخصم', headerEn: 'Discount', value: r => r.discounT_AMOUNT || 0 },
        { headerAr: 'الضريبة', headerEn: 'VAT', value: r => r.vat || 0 },
        { headerAr: 'الصافي', headerEn: 'Net', value: r => r.neT_AMOUNT || 0 },
        { headerAr: 'الوصف', headerEn: 'Description', value: r => r.description || '' }
      ];
      return cols as any;
    }

    // fallback dynamic for other views
    const first = itemsAny?.[0];
    const keys = first ? Object.keys(first) : [];
    return keys.map(k => ({
      headerAr: k,
      headerEn: k,
      value: (r: any) => {
        const v = r?.[k];
        if (typeof v === 'number') return v;
        return safe(v, '');
      }
    }));
  }, [isDetail, itemsAny, isAr]);

  useEffect(() => {
    const initialRequest: PurTransactionsRequestDto = {
      pageIndex: 0,
      pageSize: 50,
      view: 'DETAIL'
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );
  }, [dispatch]);

  const applyFilters = () => {
    const branchId = getOptId(branchFilter);
    const peopleId = getOptId(supplierFilter);

    const fd = isoOrNull(fromDate);
    const td = isoOrNull(toDate);

    const prevReq = (((report as any)?.request ?? {}) as PurTransactionsRequestDto) || {};

    const nextReq: PurTransactionsRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,

      view: currentView,

      // modern
      fromDate: fd,
      toDate: td,
      branchId,
      inventoryId: inventoryId || null,
      peopleId,
      transTypeIds: transTypeIds || null,

      // legacy duplicates
      froM_DATE: fd,
      tO_DATE: td,
      brancH_ID: branchId,
      inventorY_ID: inventoryId || null,
      peoplE_ID: peopleId,
      tranS_TYPE_IDS: transTypeIds || null
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setView(viewOptions[0]);
    setBranchFilter(null);
    setSupplierFilter(null);
    setInventoryId('');
    setTransTypeIds('');
    setFromDate(null);
    setToDate(null);

    const resetReq: PurTransactionsRequestDto = {
      pageIndex: 0,
      pageSize: 50,
      view: 'DETAIL'
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
  };

  // ===== Totals (cards): prefer meta totals if exists, else page totals =====
  const pageTotals = useMemo(() => {
    if (!isDetail) return { gross: 0, discount: 0, vat: 0, net: 0 };
    return itemsDetail.reduce(
      (acc, r) => {
        acc.gross += r.grosS_AMOUNT || 0;
        acc.discount += r.discounT_AMOUNT || 0;
        acc.vat += r.vat || 0;
        acc.net += r.neT_AMOUNT || 0;
        return acc;
      },
      { gross: 0, discount: 0, vat: 0, net: 0 }
    );
  }, [itemsDetail, isDetail]);

  const metaTotals = useMemo(() => {
    // keys احتمال تتغير عندك، عشان كذا حاطين أكثر من احتمال
    const gross = pickNum(meta, ['total_GROSS_AMOUNT', 'totalGrossAmount', 'total_GROSS', 'totalGross']);
    const discount = pickNum(meta, ['total_DISCOUNT_AMOUNT', 'totalDiscountAmount', 'total_DISCOUNT', 'totalDiscount']);
    const vat = pickNum(meta, ['total_VAT', 'totalVat', 'total_vat']);
    const net = pickNum(meta, ['total_NET_AMOUNT', 'totalNetAmount', 'total_NET', 'totalNet']);
    const hasAny = gross !== 0 || discount !== 0 || vat !== 0 || net !== 0;
    return { gross, discount, vat, net, hasAny };
  }, [meta]);

  const TotalsCards = () => {
    if (!isDetail) return null;

    const useMeta = metaTotals.hasAny;
    const gross = useMeta ? metaTotals.gross : pageTotals.gross;
    const discount = useMeta ? metaTotals.discount : pageTotals.discount;
    const vat = useMeta ? metaTotals.vat : pageTotals.vat;
    const net = useMeta ? metaTotals.net : pageTotals.net;

    if (itemsAny.length === 0 && !useMeta) return null;

    return (
      <div className='row g-3 mb-3'>
        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-layer-group text-primary fa-xl'></i>
              </div>
              <div>
                <h6 className='text-primary fw-bold mb-1'>{isAr ? 'إجمالي' : 'Gross Total'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(gross)}</h4>
              </div>
            </div>
          </Card>
        </div>

        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-danger bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-tags text-danger fa-xl'></i>
              </div>
              <div>
                <h6 className='text-danger fw-bold mb-1'>{isAr ? 'إجمالي الخصم' : 'Total Discount'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(discount)}</h4>
              </div>
            </div>
          </Card>
        </div>

        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-warning bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-percent text-warning fa-xl'></i>
              </div>
              <div>
                <h6 className='text-warning fw-bold mb-1'>{isAr ? 'إجمالي الضريبة' : 'Total VAT'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(vat)}</h4>
              </div>
            </div>
          </Card>
        </div>

        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-success bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-coins text-success fa-xl'></i>
              </div>
              <div>
                <h6 className='text-success fw-bold mb-1'>{isAr ? 'إجمالي الصافي' : 'Net Total'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(net)}</h4>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // ===== Generic Table for non-detail views =====
  const dynamicColumns = useMemo(() => {
    if (isDetail) return [];
    const first = itemsAny?.[0];
    if (!first) return [];
    return Object.keys(first);
  }, [itemsAny, isDetail]);

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* View (Required) */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-layer-group text-warning'></i>
                <span className='text-danger ms-1'>*</span>
              </span>
              <Select
                value={view}
                onChange={setView}
                options={viewOptions}
                placeholder={isAr ? 'نوع العرض' : 'View'}
                isClearable={false}
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Branch */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-store text-primary'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={branchFilter}
                onChange={setBranchFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع' : 'Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Supplier */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-truck text-danger'></i>
              </span>
              <Select
                isLoading={loadingSuppliers}
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={OptionsSuppliers}
                placeholder={isAr ? 'المورد' : 'Supplier'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-3 text-end'>
            <button className='btn btn-outline-secondary me-2' type='button' onClick={clearFilters} title={t('ClearFilters')}>
              <i className='fas fa-times fa-fw'></i>
            </button>

            <button className='btn btn-theme me-2' type='button' onClick={applyFilters} title={t('ApplyFilters')}>
              <i className='fas fa-filter fa-fw'></i>
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || itemsAny.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* From */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* To */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>

          {/* InventoryId */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-warehouse text-info'></i>
              </span>
              <input
                type='text'
                className='form-control'
                value={inventoryId}
                onChange={e => setInventoryId(e.target.value)}
                placeholder={isAr ? 'InventoryId (GUID) - اختياري' : 'InventoryId (GUID) - optional'}
              />
            </div>
          </div>

          {/* TransTypeIds */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-list text-secondary'></i>
              </span>
              <input
                type='text'
                className='form-control'
                value={transTypeIds}
                onChange={e => setTransTypeIds(e.target.value)}
                placeholder={isAr ? 'TransTypeIds (CSV GUIDs) - اختياري' : 'TransTypeIds (CSV GUIDs) - optional'}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      {/* ✅ Totals Cards (DETAIL only) */}
      {/* <TotalsCards /> */}

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            {/* {isDetail ? ( */}
            {true ? (
              <table className='table table-hover table-bordered mb-0'>
                <thead className='sticky-top align-middle' style={{ top: 0, zIndex: 10 }}>
                  <tr className='bg-theme bg-opacity-35 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                    <th className='small text-primary' colSpan={6}>
                      {isAr ? 'بيانات العملية' : 'Transaction Info'}
                    </th>
                    <th className='small text-success' colSpan={4}>
                      {isAr ? 'المبالغ' : 'Amounts'}
                    </th>
                    <th className='small' rowSpan={2} style={{ minWidth: 260 }}>
                      {isAr ? 'الوصف' : 'Description'}
                    </th>
                  </tr>

                  <tr className='bg-theme bg-opacity-25 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                    <th className='small' style={{ minWidth: 120 }}>
                      {t('Date')}
                    </th>
                    <th className='small' style={{ minWidth: 160 }}>
                      {isAr ? 'الكود' : 'Code'}
                    </th>
                    <th className='small' style={{ minWidth: 170 }}>
                      {isAr ? 'النوع' : 'Type'}
                    </th>
                    <th className='small' style={{ minWidth: 170 }}>
                      {isAr ? 'الفرع' : 'Branch'}
                    </th>
                    <th className='small' style={{ minWidth: 220 }}>
                      {isAr ? 'المخزن' : 'Inventory'}
                    </th>
                    <th className='small' style={{ minWidth: 220 }}>
                      {isAr ? 'المورد' : 'Supplier'}
                    </th>

                    <th className='small text-end' style={{ minWidth: 120 }}>
                      {isAr ? 'الإجمالي' : 'Gross'}
                    </th>
                    <th className='small text-end' style={{ minWidth: 110 }}>
                      {isAr ? 'الخصم' : 'Discount'}
                    </th>
                    <th className='small text-end' style={{ minWidth: 110 }}>
                      {isAr ? 'الضريبة' : 'VAT'}
                    </th>
                    <th className='small text-end' style={{ minWidth: 120 }}>
                      {isAr ? 'الصافي' : 'Net'}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {!loading && itemsDetail.length > 0 ? (
                    itemsDetail.map((r, idx) => {
                      const typeName = isAr ? r.typE_NAME1 || r.typE_NAME2 : r.typE_NAME2 || r.typE_NAME1;
                      const branchName = isAr ? r.brancH_NAME2 || r.brancH_NAME : r.brancH_NAME || r.brancH_NAME2;
                      const invName = isAr ? r.inventorY_NAME2 || r.inventorY_NAME : r.inventorY_NAME || r.inventorY_NAME2;
                      const suppName = isAr ? r.peoplE_NAME2 || r.peoplE_NAME : r.peoplE_NAME || r.peoplE_NAME2;

                      return (
                        <tr key={`${r.id || idx}`} className='align-middle'>
                          <td className='text-center'>{formatDate(r.transactioN_DATE)}</td>
                          <td className='text-center'>
                            <span className='fw-bold text-primary'>{safe(r.code)}</span>
                          </td>
                          <td className='text-start'>{safe(typeName)}</td>
                          <td className='text-start'>{safe(branchName)}</td>
                          <td className='text-start'>{safe(invName)}</td>
                          <td className='text-start'>
                            <div className='fw-medium'>{safe(suppName)}</div>
                            {r.peoplE_NUMBER ? <div className='small text-muted'>{r.peoplE_NUMBER}</div> : null}
                          </td>

                          <td className='text-end fw-bold'>{formatNumber(r.grosS_AMOUNT)}</td>
                          <td className='text-end fw-bold text-danger'>{formatNumber(r.discounT_AMOUNT)}</td>
                          <td className='text-end fw-bold text-warning'>{formatNumber(r.vat)}</td>
                          <td className='text-end fw-bold text-success'>{formatNumber(r.neT_AMOUNT)}</td>

                          <td className='text-start'>{safe(r.description, isAr ? '—' : '—')}</td>
                        </tr>
                      );
                    })
                  ) : !loading ? (
                    <tr>
                      <td colSpan={11} className='text-center py-5'>
                        <div className='text-muted'>
                          <i className='fas fa-file-invoice fa-3x mb-3 opacity-50'></i>
                          <h6>{t('NoData')}</h6>
                          <small>{t('TryChangingFilters')}</small>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>

                {/* {!loading && itemsDetail.length > 0 && (
                  <tfoot className='fw-bold bg-secondary bg-opacity-30'>
                    <tr>
                      <td colSpan={6} className='text-end'>
                        <span className='h6 m-0'>{isAr ? 'الإجمالي (الصفحة):' : 'Totals (page):'}</span>
                      </td>
                      <td className='text-end'>
                        <span className='h6 m-0'>{formatNumber(pageTotals.gross)}</span>
                      </td>
                      <td className='text-end text-danger'>
                        <span className='h6 m-0'>{formatNumber(pageTotals.discount)}</span>
                      </td>
                      <td className='text-end text-warning'>
                        <span className='h6 m-0'>{formatNumber(pageTotals.vat)}</span>
                      </td>
                      <td className='text-end text-success'>
                        <span className='h6 m-0'>{formatNumber(pageTotals.net)}</span>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )} */}
              </table>
            ) : (
              <table className='table table-hover table-bordered mb-0'>
                <thead className='sticky-top align-middle' style={{ top: 0, zIndex: 10 }}>
                  <tr className='bg-theme bg-opacity-35 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                    {dynamicColumns.length > 0 ? (
                      dynamicColumns.map(k => (
                        <th key={k} className='small' style={{ minWidth: 140 }}>
                          {k}
                        </th>
                      ))
                    ) : (
                      <th className='small'>{isAr ? 'لا توجد بيانات' : 'No data'}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!loading && itemsAny.length > 0 ? (
                    itemsAny.map((row, idx) => (
                      <tr key={idx} className='align-middle'>
                        {dynamicColumns.map(col => {
                          const v = row?.[col];
                          const isNum = typeof v === 'number';
                          const isDate = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);
                          return (
                            <td key={col} className={isNum ? 'text-end' : 'text-start'}>
                              {isNum ? formatNumber(v) : isDate ? formatDate(v) : safe(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : !loading ? (
                    <tr>
                      <td colSpan={Math.max(dynamicColumns.length, 1)} className='text-center py-5'>
                        <div className='text-muted'>
                          <i className='fas fa-file-invoice fa-3x mb-3 opacity-50'></i>
                          <h6>{t('NoData')}</h6>
                          <small>{t('TryChangingFilters')}</small>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />
            {!loading && itemsAny.length > 0 && (
              <div className='text-muted small'>
                <i className='fas fa-info-circle me-1'></i>
                {isAr ? 'عرض' : 'Showing'} {itemsAny.length} {isAr ? 'من' : 'of'} {meta?.totalRows || 0}{' '}
                {isAr ? 'سجل' : 'records'}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'حركات المشتريات' : 'Purchase Transactions')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default PurTransactions_Paged;